import React, { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'

import EditableTable from '@/components/Table/EditableTable'
import IndeterminateCheckbox from '@/components/Table/components/IndeterminateCheckbox'
import DownloadButton from '@/components/Elements/Button/DownloadButton'
import { FileInput, Textarea } from '@/components/Form'
import { ApprovalInvitationStatus, OfferDocumentTypes } from '@/features/offers/constants'
import { getRefId } from '@/features/offers/offer-detail/helper/approverSlotMatching.helper'
import useOfferGCPR from '@/features/offers/hooks/useOfferGCPR'
import useOfferPRApproval from '@/features/offers/hooks/useOfferPRApproval'
import useOfferBookingApproval from '@/features/offers/hooks/useOfferBookingApproval'
import useOfferQuotationApproval from '@/features/offers/hooks/useOfferQuotationApproval'
import { authSelector } from '@/stores/auth/authSlice'
import { setApprovalInvitedUsers, selectApprovalInvitedUsers } from '@/stores/offers/offersSlice'
import { showModal, hideModal } from '@/stores/ui/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { createColumnHelper } from '@tanstack/react-table'
import { APPROVAL_TABLE_TYPE } from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import { toast } from 'react-toastify'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

const statusColorMap = {
  Invited: 'yellow',
  Replied: 'green',
  'Not Required': 'gray',
}

const normalizeReplyFiles = (files) => {
  if (Array.isArray(files)) {
    return files.map((file) => file?._id || file).filter((id) => id)
  }
  if (files?._id) {
    return [files._id]
  }
  return files ? [files] : []
}

const InvitationDetailModal = ({ canManageInvitations, updateInvitationParameter, approvalRole, tableType }) => {
  const invitedUsers = useSelector(selectApprovalInvitedUsers)
  const { user: currentUser } = useSelector(authSelector)

  const dispatch = useDispatch()
  const [rowSelection, setRowSelection] = useState({})

  const { useDeletePRInvitedUsers, useReplyPRInvitation } = useOfferPRApproval()
  const { useDeleteBookingInvitedUsers, useReplyBookingInvitation } = useOfferBookingApproval()
  const { useDeleteQuotationInvitedUsers, useReplyQuotationInvitation } = useOfferQuotationApproval()
  const { useDeleteGCPRInvitedUser, useReplyGCPRInvitation } = useOfferGCPR()

  const deletePRInvitedUsers = useDeletePRInvitedUsers()
  const deleteBookingInvitedUsers = useDeleteBookingInvitedUsers()
  const deleteQuotationInvitedUsers = useDeleteQuotationInvitedUsers()
  const deleteGCPRInvitedUser = useDeleteGCPRInvitedUser()

  const replyPRInvitation = useReplyPRInvitation()
  const replyBookingInvitation = useReplyBookingInvitation()
  const replyQuotationInvitation = useReplyQuotationInvitation()
  const replyGCPRInvitation = useReplyGCPRInvitation()

  const methods = useForm({ defaultValues: { remark: '', files: [] } })

  // At most one row can ever belong to the current viewer as a pending invitee -
  // that is the only row the inline Reply/Attachment cells allow editing.
  const ownInviteeIndex = invitedUsers.findIndex(
    (invitee) => getRefId(invitee?.user) === currentUser?._id && invitee?.status === ApprovalInvitationStatus.INVITED
  )

  const columnHelper = createColumnHelper()

  const columnDef = [
    columnHelper.display({
      id: 'select',
      size: 50,
      header: ({ table }) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }) => (
        <Box px={1}>
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        </Box>
      ),
    }),
    columnHelper.accessor('user.fullName', {
      header: () => <Text>Name</Text>,
      size: 200,
    }),
    columnHelper.accessor('user.email', {
      header: () => <Text>Email</Text>,
      size: 200,
    }),
    columnHelper.accessor('status', {
      header: () => <Text>Status</Text>,
      size: 100,
      cell: ({ getValue }) => {
        const status = getValue()
        return <Badge colorScheme={statusColorMap[status] || 'gray'}>{status}</Badge>
      },
    }),
    columnHelper.accessor('remark', {
      header: () => <Text>Invitation Remark</Text>,
      size: 300,
    }),
    columnHelper.accessor('files', {
      header: () => <Text>Files Sent</Text>,
      size: 320,
      cell: ({ getValue }) => {
        const files = getValue()

        if (!Array.isArray(files) || files.length === 0) {
          return <Text color="gray.500">-</Text>
        }

        return (
          <Flex gap="2" direction="column" align="flex-start">
            {files.map((file, index) => {
              const fileId = file?._id || file
              const fileName = file?.originalFileName || file?.fileName || `invitation_attachment_${index + 1}`

              return (
                <DownloadButton
                  key={`${fileId || 'invite-file'}-${index}`}
                  fileId={fileId}
                  fileName={fileName}
                  displayText={fileName}
                />
              )
            })}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('reply', {
      header: () => <Text>Reply</Text>,
      size: 300,
      cell: ({ row, getValue }) => {
        if (row.index !== ownInviteeIndex) {
          return <Text>{getValue() || '-'}</Text>
        }

        return <Textarea name="remark" row={3} />
      },
    }),
    columnHelper.accessor('replyFiles', {
      header: () => <Text>Reply Attachment</Text>,
      size: 320,
      cell: ({ row, getValue }) => {
        if (row.index !== ownInviteeIndex) {
          const files = getValue()

          if (!Array.isArray(files) || files.length === 0) {
            return <Text color="gray.500">-</Text>
          }

          return (
            <Flex gap="2" direction="column" align="flex-start">
              {files.map((file, index) => {
                const fileId = file?._id || file
                const fileName = file?.originalFileName || file?.fileName || `reply_attachment_${index + 1}`

                return (
                  <DownloadButton
                    key={`${fileId || 'reply-file'}-${index}`}
                    fileId={fileId}
                    fileName={fileName}
                    displayText={fileName}
                  />
                )
              })}
            </Flex>
          )
        }

        return (
          <FileInput
            label="Attachment"
            name="files"
            fileType={OfferDocumentTypes.SOURCING_INTERNAL_DOCUMENT}
            isRow
            canUpload
            isMultiple
            isRequired={false}
          />
        )
      },
    }),
  ]

  const handleOpenAddModal = () => {
    if (!canManageInvitations) return

    dispatch(
      showModal({
        modalType: MODAL_TYPES.ADD_INVITATION,
        modalProps: {
          tableType: tableType,
          ...updateInvitationParameter,
        },
      })
    )
  }

  const handleDeleteUsers = async () => {
    if (!canManageInvitations) return

    const selectedRowIndexes = Object.keys(rowSelection)
      .map((index) => Number(index))
      .filter((index) => Number.isInteger(index))

    if (selectedRowIndexes.length === 0) return

    const removedUserIds = selectedRowIndexes.map((index) => invitedUsers[index]?.user?._id).filter(Boolean)

    try {
      dispatch(showLoadingOverlay())

      switch (tableType) {
        case APPROVAL_TABLE_TYPE.PR_APPROVAL:
          {
            await deletePRInvitedUsers.mutateAsync({
              ...updateInvitationParameter,
              userIds: removedUserIds,
            })
          }
          break
        case APPROVAL_TABLE_TYPE.BOOKING_APPROVAL:
          {
            await deleteBookingInvitedUsers.mutateAsync({
              offerId: updateInvitationParameter?.offerId,
              approverIndex: updateInvitationParameter?.approverIndex,
              userIds: removedUserIds,
            })
          }
          break
        case APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL:
          {
            await deleteQuotationInvitedUsers.mutateAsync({
              offerId: updateInvitationParameter?.offerId,
              approverIndex: updateInvitationParameter?.approverIndex,
              userIds: removedUserIds,
            })
          }
          break
        case APPROVAL_TABLE_TYPE.GCPR_APPROVAL:
          {
            const sortedInviteeIndexes = [...selectedRowIndexes].sort((left, right) => right - left)
            for (const inviteeIndex of sortedInviteeIndexes) {
              await deleteGCPRInvitedUser.mutateAsync({
                offerId: updateInvitationParameter?.offerId,
                submissionIndex: updateInvitationParameter?.submissionIndex,
                approverIndex: updateInvitationParameter?.approverIndex,
                inviteeIndex,
              })
            }
          }
          break
        default:
          throw new Error('Unsupported table type for deleting invited users')
      }
      if (tableType === APPROVAL_TABLE_TYPE.GCPR_APPROVAL) {
        const selectedIndexesSet = new Set(selectedRowIndexes)
        dispatch(setApprovalInvitedUsers(invitedUsers.filter((_, index) => !selectedIndexesSet.has(index))))
      } else {
        const removedUserIds = selectedRowIndexes.map((index) => invitedUsers[index]?.user?._id).filter(Boolean)
        dispatch(setApprovalInvitedUsers(invitedUsers.filter((detail) => !removedUserIds.includes(detail?.user?._id))))
      }
      setRowSelection({})
    } catch (err) {
      toast.error(err.message)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleSubmitReply = async ({ remark, files }) => {
    if (ownInviteeIndex < 0) {
      return
    }

    if (!remark || remark === '') {
      toast.error('Please enter a remark!')
      return
    }

    const normalizedFiles = normalizeReplyFiles(files)

    try {
      dispatch(showLoadingOverlay())

      switch (tableType) {
        case APPROVAL_TABLE_TYPE.PR_APPROVAL:
          await replyPRInvitation.mutateAsync({
            ...updateInvitationParameter,
            inviteeIndex: ownInviteeIndex,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.BOOKING_APPROVAL:
          await replyBookingInvitation.mutateAsync({
            offerId: updateInvitationParameter?.offerId,
            approverIndex: updateInvitationParameter?.approverIndex,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.QUOTATION_APPROVAL:
          await replyQuotationInvitation.mutateAsync({
            offerId: updateInvitationParameter?.offerId,
            approverIndex: updateInvitationParameter?.approverIndex,
            remark,
            files: normalizedFiles,
          })
          break
        case APPROVAL_TABLE_TYPE.GCPR_APPROVAL:
          await replyGCPRInvitation.mutateAsync({
            offerId: updateInvitationParameter?.offerId,
            submissionIndex: updateInvitationParameter?.submissionIndex,
            approverIndex: updateInvitationParameter?.approverIndex,
            inviteeIndex: ownInviteeIndex,
            remark,
            files: normalizedFiles,
          })
          break
        default:
          throw new Error('Unsupported table type for invitation reply')
      }

      dispatch(
        setApprovalInvitedUsers(
          invitedUsers.map((detail, index) =>
            index === ownInviteeIndex
              ? { ...detail, status: ApprovalInvitationStatus.REPLIED, reply: remark, replyFiles: normalizedFiles }
              : detail
          )
        )
      )
      methods.reset({ remark: '', files: [] })
    } catch (err) {
      toast.error(err.message)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const closeModal = () => {
    dispatch(hideModal())
  }

  return (
    <Modal isOpen={true} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW="80%">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmitReply)}>
            <ModalHeader>Approval Invitation — {approvalRole}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {canManageInvitations && (
                <HStack mb={4}>
                  <Button colorScheme="green" size="sm" onClick={handleOpenAddModal}>
                    Invite
                  </Button>
                  <Button
                    colorScheme="red"
                    size="sm"
                    onClick={handleDeleteUsers}
                    isDisabled={Object.keys(rowSelection).length === 0}
                    isLoading={
                      deletePRInvitedUsers.isLoading ||
                      deleteBookingInvitedUsers.isLoading ||
                      deleteQuotationInvitedUsers.isLoading ||
                      deleteGCPRInvitedUser.isLoading
                    }
                  >
                    Remove
                  </Button>
                </HStack>
              )}
              <EditableTable
                columnDef={columnDef}
                data={invitedUsers}
                setRowSelection={setRowSelection}
                rowSelection={rowSelection}
                canSelect={canManageInvitations}
              />
            </ModalBody>
            <ModalFooter>
              <HStack>
                {ownInviteeIndex >= 0 && (
                  <Button
                    colorScheme="green"
                    type="submit"
                    isLoading={
                      replyPRInvitation.isLoading ||
                      replyBookingInvitation.isLoading ||
                      replyQuotationInvitation.isLoading ||
                      replyGCPRInvitation.isLoading
                    }
                  >
                    Submit Reply
                  </Button>
                )}
                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default InvitationDetailModal
